import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { statsApi, clientsApi } from '../services/api';
import { toast } from 'react-hot-toast';
import { useAuthStore } from '../stores/authStore';
import {
  ArrowLeftIcon,
  TicketIcon,
  ChatBubbleLeftRightIcon,
  UsersIcon,
  ClockIcon,
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  MinusIcon,
  HeartIcon,
  FaceSmileIcon,
  FaceFrownIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Per-client dashboard.
 *
 * Identity in the current data model is `telegramChatId` — there's no
 * separate Client entity yet. Everything (CSAT, NPS, health, trend) is
 * aggregated server-side in /api/tickets/stats/clients/<chatId>.
 *
 * Designed to surface, at a glance, "how is this club doing right now":
 *   • Health-score (0..100) — sentiment + activity + efficiency + trend
 *   • NPS — promoter/passive/detractor split on 1..5 stars
 *   • Trend — last-7d vs prior-7d ticket count + daily sparkline
 *   • Performance — avg first-response and resolution times
 *
 * Deep-linkable: navigates fine from a ticket card click-through (we
 * fetch by chatId, no reliance on location.state).
 */

interface ClientStats {
  client: {
    chatId: string;
    chatType: string | null;
    chatTitle: string | null;
    username: string | null;
    firstName: string | null;
    lastName: string | null;
    displayName: string;
    firstActivity: string | null;
    lastActivity: string | null;
    daysSinceLastActivity: number | null;
    totalTicketsAllTime: number;
    isBlocked: boolean;
  };
  period: string;
  summary: {
    total: number;
    open: number;
    closed: number;
    byStatus: Record<'new' | 'assigned' | 'inProgress' | 'paused' | 'resolved' | 'closed', number>;
    byPriority: { urgent: number; high: number };
    escalated: number;
  };
  csat: {
    promptsSent: number;
    responses: number;
    responseRate: number;
    avgRating: number | null;
    nps: number | null;
    promoters: number;
    passives: number;
    detractors: number;
    distribution: Record<'1' | '2' | '3' | '4' | '5', number>;
    recentComments: {
      rating: number;
      comment: string;
      ticketId: string;
      ticketNumber: string;
      respondedAt: string | null;
    }[];
  };
  health: {
    score: number;
    trend: {
      direction: 'up' | 'down' | 'flat';
      deltaPct: number;
      last7d: number;
      prev7d: number;
    };
    perDay: { date: string; count: number }[];
  };
  performance: {
    avgFirstResponseSec: number | null;
    avgResolutionSec: number | null;
    escalationRate: number;
    selfResolveRate: number;
    resolvedSampleSize: number;
  };
}

const ClientDetailPage: React.FC = () => {
  const { chatId } = useParams<{ chatId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const canBlock = user?.role === 'admin' || user?.role === 'superadmin';
  const [data, setData] = useState<ClientStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [blocking, setBlocking] = useState(false);

  const toggleBlock = async () => {
    if (!data) return;
    const id = data.client.chatId;
    const currentlyBlocked = data.client.isBlocked;
    if (!currentlyBlocked) {
      const reason = window.prompt(
        'Заблокировать клиента? Бот перестанет реагировать на сообщения из этого чата.\n\nПричина (необязательно):',
        '',
      );
      // prompt returns null on Cancel — abort.
      if (reason === null) return;
      setBlocking(true);
      try {
        await statsApi.blockClient(id, reason || undefined);
        setData({ ...data, client: { ...data.client, isBlocked: true } });
        toast.success('Клиент заблокирован — бот больше не реагирует');
      } catch (e: any) {
        toast.error(e?.response?.data?.message || 'Ошибка блокировки');
      } finally {
        setBlocking(false);
      }
    } else {
      setBlocking(true);
      try {
        await statsApi.unblockClient(id);
        setData({ ...data, client: { ...data.client, isBlocked: false } });
        toast.success('Клиент разблокирован');
      } catch (e: any) {
        toast.error(e?.response?.data?.message || 'Ошибка разблокировки');
      } finally {
        setBlocking(false);
      }
    }
  };

  useEffect(() => {
    if (!chatId) return;
    let alive = true;
    setLoading(true);
    statsApi
      .getClientDetail(chatId, period)
      .then((res: any) => {
        if (!alive) return;
        if (res.data?.success) setData(res.data.data);
        else toast.error(res.data?.message || 'Ошибка загрузки клиента');
      })
      .catch((e: any) => {
        if (!alive) return;
        toast.error(e?.response?.data?.message || 'Ошибка загрузки клиента');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [chatId, period]);

  if (loading) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Загрузка карточки клиента…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="text-center py-12 space-y-3">
        <h3 className="text-lg font-medium text-foreground">Клиент не найден</h3>
        <button
          type="button"
          onClick={() => navigate('/clients')}
          className="text-primary hover:underline"
        >
          К списку клиентов
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-6xl">
      <Header
        data={data}
        period={period}
        setPeriod={setPeriod}
        onBack={() => navigate('/clients')}
        canBlock={canBlock}
        blocking={blocking}
        onToggleBlock={toggleBlock}
      />

      {data.client.isBlocked && (
        <div className="flex items-center gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-600 dark:text-rose-400">
          <NoSymbolIcon className="h-5 w-5 shrink-0" />
          Клиент заблокирован — бот игнорирует все входящие сообщения из этого чата.
        </div>
      )}

      {canBlock && chatId && <BillingCard chatId={chatId} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <HealthCard data={data} />
        <TrendCard data={data} />
        <ActivityCard data={data} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* CSAT is admin-only (backend blanks it for operators anyway). */}
        {user?.role !== 'operator' && <CSATCard data={data} />}
        <PerformanceCard data={data} />
      </div>

      <StatusBreakdown data={data} />

      <CommentsBlock data={data} />

      <div className="flex justify-center pt-2">
        <button
          type="button"
          onClick={() => navigate(`/tickets?client=${encodeURIComponent(data.client.chatId)}`)}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <TicketIcon className="h-5 w-5" />
          Все заявки клиента
        </button>
        <button
          type="button"
          onClick={() => navigate(`/clients/${encodeURIComponent(data.client.chatId)}/chat`)}
          className="ml-3 inline-flex items-center gap-2 h-11 px-5 rounded-md border border-border text-foreground hover:bg-accent"
          title="Одна лента чата с разметкой заявок — для разбора качества"
        >
          💬 Чат клуба
        </button>
      </div>
    </div>
  );
};

/* ── Sub-components ─────────────────────────────────────────────────────── */

const Header: React.FC<{
  data: ClientStats;
  period: string;
  setPeriod: (v: string) => void;
  onBack: () => void;
  canBlock: boolean;
  blocking: boolean;
  onToggleBlock: () => void;
}> = ({ data, period, setPeriod, onBack, canBlock, blocking, onToggleBlock }) => {
  const c = data.client;
  const icon = c.chatType === 'private'
    ? <UsersIcon className="h-9 w-9 text-blue-500" />
    : <ChatBubbleLeftRightIcon className="h-9 w-9 text-emerald-500" />;
  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeftIcon className="h-4 w-4" /> Назад
      </button>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {icon}
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-foreground truncate">{c.displayName}</h1>
          <p className="text-sm text-muted-foreground">
            {c.chatType === 'private' ? 'Личный чат' : 'Группа'}
            {' · '}
            всего заявок за всё время: <span className="font-medium text-foreground">{c.totalTicketsAllTime}</span>
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {canBlock && (
          <button
            type="button"
            onClick={onToggleBlock}
            disabled={blocking}
            className={`inline-flex items-center gap-1.5 h-10 px-3 rounded-md border text-sm transition-colors disabled:opacity-50 ${
              c.isBlocked
                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20'
                : 'border-rose-500/30 bg-rose-500/10 text-rose-600 hover:bg-rose-500/20'
            }`}
            title={c.isBlocked ? 'Снять блокировку' : 'Бот перестанет реагировать на этот чат'}
          >
            {c.isBlocked
              ? <><CheckCircleIcon className="h-4 w-4" /> Разблокировать</>
              : <><NoSymbolIcon className="h-4 w-4" /> Заблокировать</>}
          </button>
        )}
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="h-10 px-3 rounded-md border border-border bg-card text-sm text-foreground"
          aria-label="Период"
        >
          <option value="7d">7 дней</option>
          <option value="30d">30 дней</option>
          <option value="90d">90 дней</option>
          <option value="1y">1 год</option>
        </select>
      </div>
    </div>
  );
};

const healthTone = (score: number) => {
  if (score >= 75) return { ring: 'ring-emerald-500/40', text: 'text-emerald-500', label: 'Здоров', tone: 'bg-emerald-500/10' };
  if (score >= 50) return { ring: 'ring-amber-500/40',   text: 'text-amber-500',   label: 'В норме',  tone: 'bg-amber-500/10' };
  if (score >= 25) return { ring: 'ring-orange-500/40',  text: 'text-orange-500',  label: 'Зона риска', tone: 'bg-orange-500/10' };
  return { ring: 'ring-rose-500/40', text: 'text-rose-500', label: 'Критично', tone: 'bg-rose-500/10' };
};

const HealthCard: React.FC<{ data: ClientStats }> = ({ data }) => {
  const score = data.health.score;
  const t = healthTone(score);
  // SVG gauge with stroke-dashoffset for a clean ring without dep.
  const r = 42;
  const c = 2 * Math.PI * r;
  const offset = c - (score / 100) * c;
  return (
    <div className="bg-card border border-border rounded-lg p-5 flex flex-col items-center justify-center gap-3">
      <p className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1">
        <HeartIcon className="h-3.5 w-3.5" /> Health-score
      </p>
      <div className={`relative ${t.tone} rounded-full p-2`}>
        <svg width="110" height="110" viewBox="0 0 110 110">
          <circle cx="55" cy="55" r={r} className="stroke-border" strokeWidth="8" fill="none" />
          <circle
            cx="55" cy="55" r={r}
            className={t.text} strokeWidth="8" fill="none"
            strokeDasharray={c} strokeDashoffset={offset}
            transform="rotate(-90 55 55)"
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.6s ease' }}
          />
          <text x="55" y="60" textAnchor="middle" className={`fill-foreground font-bold text-2xl`}>
            {score}
          </text>
        </svg>
      </div>
      <p className={`text-sm font-semibold ${t.text}`}>{t.label}</p>
      <p className="text-[11px] text-muted-foreground text-center leading-snug">
        Композит: CSAT&nbsp;·&nbsp;активность&nbsp;·&nbsp;эскалации&nbsp;·&nbsp;тренд
      </p>
    </div>
  );
};

const TrendCard: React.FC<{ data: ClientStats }> = ({ data }) => {
  const trend = data.health.trend;
  const dir = trend.direction;
  // "Down" in ticket counts is a GOOD signal in support context — fewer problems.
  const good = dir === 'down' || (dir === 'flat' && Math.abs(trend.deltaPct) < 10);
  const Icon = dir === 'up' ? ArrowTrendingUpIcon
    : dir === 'down' ? ArrowTrendingDownIcon
    : MinusIcon;
  const color = good ? 'text-emerald-500' : (dir === 'up' ? 'text-rose-500' : 'text-amber-500');
  const tone = good ? 'bg-emerald-500/10' : (dir === 'up' ? 'bg-rose-500/10' : 'bg-amber-500/10');

  return (
    <div className="bg-card border border-border rounded-lg p-5 flex flex-col gap-3">
      <p className="text-xs text-muted-foreground uppercase tracking-wide">Тренд обращений</p>
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-md ${tone}`}>
          <Icon className={`h-7 w-7 ${color}`} />
        </div>
        <div>
          <p className={`text-2xl font-bold ${color}`}>
            {trend.deltaPct > 0 ? '+' : ''}{trend.deltaPct}%
          </p>
          <p className="text-[11px] text-muted-foreground">7 дней vs предыдущие 7</p>
        </div>
      </div>
      <Sparkline points={data.health.perDay} color={color} />
      <p className="text-[11px] text-muted-foreground">
        За 7 дней: <span className="font-medium text-foreground">{trend.last7d}</span>
        {' · '}за прошлые 7: <span className="font-medium text-foreground">{trend.prev7d}</span>
      </p>
    </div>
  );
};

const Sparkline: React.FC<{ points: { date: string; count: number }[]; color: string }> = ({ points, color }) => {
  if (!points.length) return <div className="text-[11px] text-muted-foreground italic">Нет данных за период</div>;
  const w = 240; const h = 48; const pad = 2;
  const max = Math.max(...points.map(p => p.count), 1);
  const step = points.length > 1 ? (w - pad * 2) / (points.length - 1) : 0;
  const d = points.map((p, i) => {
    const x = pad + i * step;
    const y = h - pad - (p.count / max) * (h - pad * 2);
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg width={w} height={h} className="overflow-visible">
      <path d={d} fill="none" strokeWidth="2" className={color} stroke="currentColor" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

const ActivityCard: React.FC<{ data: ClientStats }> = ({ data }) => {
  const c = data.client;
  const dsl = c.daysSinceLastActivity;
  const tone =
    dsl === null      ? 'text-muted-foreground'
    : dsl <= 7        ? 'text-emerald-500'
    : dsl <= 30       ? 'text-amber-500'
    : dsl <= 60       ? 'text-orange-500'
    :                   'text-rose-500';
  return (
    <div className="bg-card border border-border rounded-lg p-5 flex flex-col gap-3">
      <p className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1">
        <ClockIcon className="h-3.5 w-3.5" /> Активность
      </p>
      <div>
        <p className={`text-2xl font-bold ${tone}`}>
          {dsl === null ? '—' : dsl === 0 ? 'сегодня' : `${dsl} дн. назад`}
        </p>
        <p className="text-[11px] text-muted-foreground">Последнее обращение</p>
      </div>
      <div className="text-[11px] text-muted-foreground space-y-0.5">
        <p>Первое обращение: <span className="text-foreground">{c.firstActivity ? new Date(c.firstActivity).toLocaleDateString('ru-RU') : '—'}</span></p>
        <p>Открытых заявок сейчас: <span className="text-foreground font-medium">{data.summary.open}</span></p>
      </div>
    </div>
  );
};

const npsTone = (nps: number | null) => {
  if (nps === null) return 'text-muted-foreground';
  if (nps >= 50) return 'text-emerald-500';
  if (nps >= 0)  return 'text-amber-500';
  return 'text-rose-500';
};

const CSATCard: React.FC<{ data: ClientStats }> = ({ data }) => {
  const csat = data.csat;
  const dist = csat.distribution;
  const maxBar = Math.max(...Object.values(dist), 1);
  return (
    <div className="bg-card border border-border rounded-lg p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <FaceSmileIcon className="h-4 w-4 text-emerald-500" />
          CSAT и NPS
        </h3>
        <span className="text-[11px] text-muted-foreground">
          ответили {csat.responses} из {csat.promptsSent}
          {csat.promptsSent > 0 && ` (${Math.round(csat.responseRate * 100)}%)`}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="border border-border rounded-md p-3 text-center">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Средняя оценка</p>
          <p className="text-3xl font-bold text-foreground mt-1">
            {csat.avgRating !== null ? csat.avgRating.toFixed(2) : '—'}
            <span className="text-base text-muted-foreground"> / 5</span>
          </p>
          <p className="text-[10px] text-muted-foreground mt-1">
            {csat.responses ? `${csat.responses} ответов` : 'нет данных'}
          </p>
        </div>
        <div className="border border-border rounded-md p-3 text-center">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">NPS</p>
          <p className={`text-3xl font-bold mt-1 ${npsTone(csat.nps)}`}>
            {csat.nps !== null ? (csat.nps > 0 ? `+${csat.nps}` : csat.nps) : '—'}
          </p>
          <p className="text-[10px] text-muted-foreground mt-1">
            {csat.responses ? 'продвигают − недовольны' : 'нет данных'}
          </p>
        </div>
      </div>

      {/* NPS split bar */}
      {csat.responses > 0 && (
        <div className="space-y-1">
          <div className="h-2 rounded-full overflow-hidden flex bg-muted">
            <div className="bg-emerald-500" style={{ width: `${(csat.promoters / csat.responses) * 100}%` }} title={`Промоутеры: ${csat.promoters}`} />
            <div className="bg-amber-500"   style={{ width: `${(csat.passives / csat.responses) * 100}%` }}  title={`Нейтральные: ${csat.passives}`} />
            <div className="bg-rose-500"    style={{ width: `${(csat.detractors / csat.responses) * 100}%` }} title={`Недовольны: ${csat.detractors}`} />
          </div>
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span className="text-emerald-600">⭐ 5 — {csat.promoters}</span>
            <span className="text-amber-600">⭐ 3–4 — {csat.passives}</span>
            <span className="text-rose-600">⭐ 1–2 — {csat.detractors}</span>
          </div>
        </div>
      )}

      {/* Distribution histogram.
          The star column is a single ⭐ + number (e.g. "5 ⭐") — repeating
          the emoji 5 times overflowed the column and visually crushed the
          bar. Compact label + wider bar reads much faster. */}
      <div className="space-y-1.5">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Распределение оценок</p>
        {(['5', '4', '3', '2', '1'] as const).map(k => {
          const v = dist[k];
          const pct = (v / maxBar) * 100;
          const color = k === '5' || k === '4' ? 'bg-emerald-500'
                      : k === '3' ? 'bg-amber-500'
                      : 'bg-rose-500';
          const textColor = k === '5' || k === '4' ? 'text-emerald-500'
                          : k === '3' ? 'text-amber-500'
                          : 'text-rose-500';
          return (
            <div key={k} className="flex items-center gap-2 text-xs">
              <span className={`shrink-0 inline-flex items-center gap-0.5 w-10 font-medium ${textColor}`}>
                {k}<span className="text-[11px]">⭐</span>
              </span>
              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                <div className={`${color} h-full rounded-full transition-all duration-300`} style={{ width: `${pct}%` }} />
              </div>
              <span className="shrink-0 w-6 text-right text-foreground font-medium tabular-nums">{v}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const fmtDuration = (seconds: number | null) => {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 60) return `${seconds} с`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} мин`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)} ч`;
  return `${(seconds / 86400).toFixed(1)} дн`;
};

const PerformanceCard: React.FC<{ data: ClientStats }> = ({ data }) => {
  const p = data.performance;
  const escTone = p.escalationRate >= 0.25 ? 'text-rose-500'
              : p.escalationRate >= 0.10 ? 'text-amber-500'
              : 'text-emerald-500';
  return (
    <div className="bg-card border border-border rounded-lg p-5 space-y-4">
      <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
        <ClockIcon className="h-4 w-4 text-primary" />
        Скорость и эскалации
      </h3>
      <div className="grid grid-cols-2 gap-3">
        <div className="border border-border rounded-md p-3">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Время до первого ответа</p>
          <p className="text-2xl font-bold text-foreground mt-1">{fmtDuration(p.avgFirstResponseSec)}</p>
          <p className="text-[10px] text-muted-foreground mt-1">среднее по {p.resolvedSampleSize} закрытым</p>
        </div>
        <div className="border border-border rounded-md p-3">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Время до решения</p>
          <p className="text-2xl font-bold text-foreground mt-1">{fmtDuration(p.avgResolutionSec)}</p>
          <p className="text-[10px] text-muted-foreground mt-1">среднее по {p.resolvedSampleSize} закрытым</p>
        </div>
      </div>
      <div className="border border-border rounded-md p-3 flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Доля эскалаций</p>
          <p className={`text-2xl font-bold mt-1 ${escTone}`}>{Math.round(p.escalationRate * 100)}%</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Решено 1-й линией</p>
          <p className="text-2xl font-bold text-emerald-500 mt-1">{Math.round(p.selfResolveRate * 100)}%</p>
        </div>
      </div>
    </div>
  );
};

const StatusBreakdown: React.FC<{ data: ClientStats }> = ({ data }) => {
  const s = data.summary;
  const total = Math.max(s.total, 1);
  const rows = useMemo(() => [
    { key: 'new',         label: 'Новые',        v: s.byStatus.new,        cls: 'bg-amber-500' },
    { key: 'assigned',    label: 'Назначены',    v: s.byStatus.assigned,   cls: 'bg-sky-500'   },
    { key: 'inProgress',  label: 'В работе',     v: s.byStatus.inProgress, cls: 'bg-blue-500'  },
    { key: 'paused',      label: 'На паузе',     v: s.byStatus.paused,     cls: 'bg-slate-400' },
    { key: 'resolved',    label: 'Решены',       v: s.byStatus.resolved,   cls: 'bg-emerald-500' },
    { key: 'closed',      label: 'Закрыты',      v: s.byStatus.closed,     cls: 'bg-muted-foreground' },
  ], [s]);
  return (
    <div className="bg-card border border-border rounded-lg p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Тикеты за период</h3>
        <span className="text-[11px] text-muted-foreground">всего {s.total}, из них {s.escalated} эскалированы</span>
      </div>
      <div className="space-y-1.5">
        {rows.map(r => (
          <div key={r.key} className="flex items-center gap-3 text-xs">
            <span className="w-24 text-muted-foreground">{r.label}</span>
            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
              <div className={`${r.cls} h-full rounded-full transition-all duration-300`}
                   style={{ width: `${(r.v / total) * 100}%` }} />
            </div>
            <span className="w-8 text-right text-foreground font-medium">{r.v}</span>
          </div>
        ))}
      </div>
      {(s.byPriority.urgent + s.byPriority.high) > 0 && (
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground pt-2 border-t border-border">
          <ExclamationTriangleIcon className="h-4 w-4 text-rose-500" />
          Приоритет: срочных <span className="font-medium text-rose-500">{s.byPriority.urgent}</span>
          {' · '}высокий <span className="font-medium text-amber-500">{s.byPriority.high}</span>
        </div>
      )}
    </div>
  );
};

const ratingTone = (rating: number) => {
  if (rating >= 4) return 'border-emerald-500/30 bg-emerald-500/5';
  if (rating === 3) return 'border-amber-500/30 bg-amber-500/5';
  return 'border-rose-500/30 bg-rose-500/5';
};

const CommentsBlock: React.FC<{ data: ClientStats }> = ({ data }) => {
  const comments = data.csat.recentComments;
  if (!comments.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-5 space-y-3">
      <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
        <FaceFrownIcon className="h-4 w-4 text-muted-foreground" />
        Последние комментарии к оценкам
      </h3>
      <ul className="space-y-2">
        {comments.map(c => (
          <li key={c.ticketId} className={`border rounded-md p-3 text-sm ${ratingTone(c.rating)}`}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-base">{'⭐'.repeat(c.rating)}</span>
              <a href={`/tickets/${c.ticketId}`} className="text-[11px] text-muted-foreground hover:text-primary">
                #{c.ticketNumber}
                {c.respondedAt && ` · ${new Date(c.respondedAt).toLocaleDateString('ru-RU')}`}
              </a>
            </div>
            <p className="text-foreground whitespace-pre-wrap break-words">{c.comment}</p>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Admin-only billing & settings for the real Client entity (by chat id).
// Financial fields come from /api/clients/by-chat/<chatId> (admins only).
const BillingCard: React.FC<{ chatId: string }> = ({ chatId }) => {
  const CUR: Record<string, string> = { RUB: '₽', KZT: '₸', USD: '$', UZS: 'сўм', KGS: '⃀', BYN: 'Br', AMD: '֏', GEL: '₾' };
  const CURS = ['RUB', 'KZT', 'USD', 'UZS', 'KGS', 'BYN', 'AMD', 'GEL'];
  const STLABEL: Record<string, string> = { new: 'Новый', active: 'Активен', paused: 'Пауза', lost: 'Ушёл' };
  const inputCls = 'h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground';

  const [c, setC] = useState<any | null>(null);
  const [edit, setEdit] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    clientsApi.byChat(chatId)
      .then((r: any) => setC(r.data?.data?.client || null))
      .catch(() => { /* not an admin, or no client — hide */ });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [chatId]);

  if (!c) return null;

  const start = () => setEdit({
    name: c.name || '',
    telegramChatId: c.telegramChatId || '',
    pcCount: c.pcCount ?? '',
    ratePerPc: c.ratePerPc ?? '',
    tariff: c.tariff ?? '',
    currency: c.currency || 'RUB',
    billingStatus: c.billingStatus || 'new',
    ggbootEnabled: !!c.ggbootEnabled,
    ggbootRatePerPc: c.ggbootRatePerPc ?? '',
    ggbootTariff: c.ggbootTariff ?? '',
  });
  const calc = () => setEdit({ ...edit, tariff: (Number(edit.pcCount) || 0) * (Number(edit.ratePerPc) || 0) });
  const save = async () => {
    setSaving(true);
    try {
      await clientsApi.update(c.id, {
        name: edit.name,
        telegramChatId: edit.telegramChatId,
        pcCount: edit.pcCount === '' ? null : Number(edit.pcCount),
        ratePerPc: edit.ratePerPc === '' ? null : edit.ratePerPc,
        tariff: edit.tariff === '' ? null : edit.tariff,
        currency: edit.currency,
        billingStatus: edit.billingStatus,
        ggbootEnabled: edit.ggbootEnabled,
        ggbootRatePerPc: edit.ggbootRatePerPc === '' ? null : edit.ggbootRatePerPc,
        ggbootTariff: edit.ggbootTariff === '' ? null : edit.ggbootTariff,
      });
      toast.success('Сохранено');
      setEdit(null);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  const badge =
    c.billingStatus === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
      : c.billingStatus === 'new' ? 'bg-muted text-muted-foreground'
        : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';

  return (
    <div className="bg-card border border-border rounded-lg p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">💰 Биллинг и настройки <span className="text-xs text-muted-foreground">(только админ)</span></h3>
        {!edit && (
          <button type="button" onClick={start} className="h-8 px-3 rounded-md border border-input text-sm hover:bg-accent">Изменить</button>
        )}
      </div>

      {!edit ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
          <div>
            <div className="text-xs text-muted-foreground">Статус</div>
            <span className={`inline-block rounded px-2 py-0.5 text-xs ${badge}`}>{STLABEL[c.billingStatus] || c.billingStatus}</span>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Тариф/мес</div>
            <div className="font-semibold text-foreground">{c.tariff != null ? `${c.tariff} ${CUR[c.currency] || c.currency || ''}` : '—'}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Ставка × ПК</div>
            <div className="text-foreground">{(c.ratePerPc ?? '—')} × {(c.pcCount ?? '—')}</div>
          </div>
          <div className="col-span-2 sm:col-span-3 border-t border-border pt-3">
            <div className="text-xs text-muted-foreground">GGBoot (наш софт)</div>
            {c.ggbootEnabled ? (
              <div className="text-foreground">
                <span className="inline-block rounded px-2 py-0.5 text-xs bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300">включён</span>
                <span className="ml-2 font-semibold">{c.ggbootTariff != null ? `${c.ggbootTariff} ${CUR[c.currency] || c.currency || ''}/мес` : 'тариф не задан'}</span>
                <span className="ml-2 text-xs text-muted-foreground">{(c.ggbootRatePerPc ?? '—')} × {(c.pcCount ?? '—')} ПК</span>
              </div>
            ) : (
              <div className="text-muted-foreground">выключен</div>
            )}
          </div>
          <div className="col-span-2 sm:col-span-3">
            <div className="text-xs text-muted-foreground">Chat ID</div>
            <div className="text-foreground font-mono text-xs break-all">{c.telegramChatId}</div>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <label className="text-xs text-muted-foreground">Название
              <input className={inputCls} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></label>
            <label className="text-xs text-muted-foreground">Кол-во ПК
              <input type="number" className={inputCls} value={edit.pcCount}
                onChange={(e) => { const pc = e.target.value; setEdit({ ...edit, pcCount: pc, tariff: (Number(pc) || 0) * (Number(edit.ratePerPc) || 0) }); }} /></label>
            <label className="text-xs text-muted-foreground">Ставка / ПК
              <input type="number" className={inputCls} value={edit.ratePerPc}
                onChange={(e) => { const rp = e.target.value; setEdit({ ...edit, ratePerPc: rp, tariff: (Number(rp) || 0) * (Number(edit.pcCount) || 0) }); }} /></label>
            <label className="text-xs text-muted-foreground">Тариф / мес
              <div className="flex gap-1">
                <input type="number" className={inputCls} value={edit.tariff} onChange={(e) => setEdit({ ...edit, tariff: e.target.value })} />
                <button type="button" title="= ставка × ПК" onClick={calc} className="h-9 px-2 rounded-md border border-input text-sm hover:bg-accent">=</button>
              </div>
            </label>
            <label className="text-xs text-muted-foreground">Валюта
              <select className={inputCls} value={edit.currency} onChange={(e) => setEdit({ ...edit, currency: e.target.value })}>
                {CURS.map((x) => <option key={x} value={x}>{CUR[x]} {x}</option>)}
              </select></label>
            <label className="text-xs text-muted-foreground">Статус
              <select className={inputCls} value={edit.billingStatus} onChange={(e) => setEdit({ ...edit, billingStatus: e.target.value })}>
                <option value="new">Новый</option>
                <option value="active">Активен</option>
                <option value="paused">Пауза</option>
                <option value="lost">Ушёл</option>
              </select></label>
            <label className="text-xs text-muted-foreground col-span-2 sm:col-span-3">Chat ID (при смене группы)
              <input className={inputCls} value={edit.telegramChatId} onChange={(e) => setEdit({ ...edit, telegramChatId: e.target.value })} /></label>
          </div>

          {/* GGBoot — отдельная услуга, начисляется отдельной строкой каждый месяц */}
          <div className="rounded-md border border-border p-3 space-y-3">
            <label className="flex items-center gap-2 text-sm font-medium text-foreground cursor-pointer">
              <input type="checkbox" checked={edit.ggbootEnabled}
                onChange={(e) => {
                  const on = e.target.checked;
                  setEdit({
                    ...edit,
                    ggbootEnabled: on,
                    // включаем — сразу считаем тариф по ставке, если она задана
                    ggbootTariff: on && edit.ggbootTariff === '' && edit.ggbootRatePerPc !== ''
                      ? (Number(edit.ggbootRatePerPc) || 0) * (Number(edit.pcCount) || 0)
                      : edit.ggbootTariff,
                  });
                }} />
              GGBoot (наш софт) — отдельная оплата
            </label>
            {edit.ggbootEnabled && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <label className="text-xs text-muted-foreground">Ставка GGBoot / ПК
                  <input type="number" className={inputCls} value={edit.ggbootRatePerPc}
                    onChange={(e) => { const rp = e.target.value; setEdit({ ...edit, ggbootRatePerPc: rp, ggbootTariff: (Number(rp) || 0) * (Number(edit.pcCount) || 0) }); }} /></label>
                <label className="text-xs text-muted-foreground">Тариф GGBoot / мес
                  <div className="flex gap-1">
                    <input type="number" className={inputCls} value={edit.ggbootTariff}
                      onChange={(e) => setEdit({ ...edit, ggbootTariff: e.target.value })} />
                    <button type="button" title="= ставка × ПК"
                      onClick={() => setEdit({ ...edit, ggbootTariff: (Number(edit.pcCount) || 0) * (Number(edit.ggbootRatePerPc) || 0) })}
                      className="h-9 px-2 rounded-md border border-input text-sm hover:bg-accent">=</button>
                  </div></label>
                <div className="text-xs text-muted-foreground self-end pb-2">
                  Начисляется каждый месяц отдельной строкой во вкладке «GGBoot», валюта — общая с поддержкой.
                </div>
              </div>
            )}
          </div>
          <div className="text-xs text-muted-foreground">Тариф авто-считается как ставка × ПК — но можно вписать любую индивидуальную сумму. Активировать можно только с тарифом и валютой. Смена Chat ID переносит клиента на новый чат — история сохраняется.</div>
          <div className="flex gap-2">
            <button type="button" disabled={saving} onClick={save} className="h-9 px-4 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm disabled:opacity-50">Сохранить</button>
            <button type="button" onClick={() => setEdit(null)} className="h-9 px-4 rounded-md border border-input text-sm hover:bg-accent">Отмена</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDetailPage;
